import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActionsForItemService, convertMessageToHtml, DataListItemAction } from '@selfai-platform/shell';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { combineLatest, map, Observable } from 'rxjs';
import { UserFunctionCloneDialogComponent } from '../components';
import { UserFunctionsListItem } from '../models';
import { UserFunctionsDataService } from '../store';
import { UserFunctionsListDomainService } from './user-functions-list-domain.service';
import { UserFunctionsPermissionService } from './user-functions-permission.service';

@Injectable()
export class UserFunctionsActionsForItemService extends ActionsForItemService<UserFunctionsListItem> {
  public ref: DynamicDialogRef | undefined;
  constructor(
    private readonly userFunctionsListDomainService: UserFunctionsListDomainService,
    private readonly userFunctionsDataService: UserFunctionsDataService,
    private readonly userFunctionsPermissionService: UserFunctionsPermissionService,
    private readonly confirmationService: ConfirmationService,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
  ) {
    super();
  }

  override getActionsForItem(userFunction: UserFunctionsListItem): Observable<DataListItemAction[]> {
    const ownerId = userFunction.ownerId as string;

    return combineLatest({
      copyTooltipTranslate: this.translateService.get('user-functions-list.actions.copy'),
      deleteTooltipTranslate: this.translateService.get('user-functions-list.actions.delete'),
      canDelete: this.userFunctionsPermissionService.canDeleteUserFunction(ownerId),
      canClone: this.userFunctionsPermissionService.canCloneUserFunction(ownerId),
    }).pipe(
      map(({ copyTooltipTranslate, deleteTooltipTranslate, canDelete, canClone }) => {
        let items: DataListItemAction[] = [];

        if (canClone) {
          items.push({
            labelTranslate: copyTooltipTranslate,
            icon: 'pi pi-copy',
            action: () => this.clone(userFunction),
          });
        }

        if (canDelete) {
          items.push({
            labelTranslate: deleteTooltipTranslate,
            icon: 'pi pi-trash',
            action: () => this.delete(userFunction),
          });
        }

        return items;
      }),
    );
  }

  private clone(userFunctionsListItem: UserFunctionsListItem): void {
    this.ref = this.dialogService.open(UserFunctionCloneDialogComponent, {
      width: '40vw',
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: userFunctionsListItem,
    });

    this.ref.onClose.subscribe((data) => {
      if (data) {
        const sub = this.userFunctionsDataService.clone(userFunctionsListItem.id, data).subscribe((newId) => {
          this.userFunctionsDataService.add({ ...userFunctionsListItem, id: newId });
          sub.unsubscribe();
        });
      }
    });
  }

  private delete(userFunction: UserFunctionsListItem): void {
    this.confirmationService.confirm({
      key: 'dialog',
      message: convertMessageToHtml(this.translateService.instant('user-functions-list.actions.delete.confirmation')),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.userFunctionsListDomainService.deleteUserFunction(userFunction.id);
      },
    });
  }
}
